import React, { useEffect, useState } from "react";
import {
  Box,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  Pagination,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import {
  getAllServices,
  handleServiceDelete,
  handleServiceUpdate,
  handleServiceCreate,
} from "../../api-services/superAdmin";
import { Loading } from "../../components";
import { apiRequestStatuses } from "../../common/constant/index";
import { formatDateTime } from "../../utils/common";
import { useDispatch } from "react-redux";
import { notification } from "../../redux/slices/authSlice";
import {
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Circle as CircleIcon,
} from "@mui/icons-material";

const Services = () => {
  const dispatch = useDispatch();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [service, setService] = useState({
    serviceName: "", // ✅ CHANGED: was "name"
    id: "",
    imageUrl: "",
    isGeneric: true,
  });
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 0,
    totalRecords: 0,
  });

  const fetchServices = async (name = "", page = 1) => {
    try {
      const response = await getAllServices(name, page);

      const servicesData = response?.payload?.data?.services || [];
      const paginationData = response?.payload?.data?.pagination || {
        currentPage: page,
        limit: 10,
        totalRecords: 0,
        totalPages: 0,
      };

      setServices(servicesData);
      setPagination({
        currentPage: paginationData.currentPage || 1,
        limit: paginationData.limit || 10,
        totalRecords: paginationData.totalRecords || 0,
        totalPages: paginationData.totalPages || 0,
      });
    } catch (err) {
      console.error("❌ Error fetching Services:", err);
      setError("Failed to load services. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServices();
  }, []);

  // ✅ FIXED: Use serviceName consistently
  const handleClickOpen = (svc = null) => {
    console.log("🎯 handleClickOpen called with:", svc);
    setOpen(true);
    if (svc && svc._id) {
      const newState = {
        id: svc._id,
        serviceName: svc.serviceName || "",
        imageUrl: svc.imageUrl || "",
        isGeneric: svc.isGeneric ?? true,
      };
      console.log("📝 Setting service state (EDIT):", newState);
      setService(newState);
    } else {
      const newState = {
        id: "",
        serviceName: "",
        imageUrl: "",
        isGeneric: true,
      };
      console.log("📝 Setting service state (CREATE):", newState);
      setService(newState);
    }
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setService((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("📤 Submitting service state:", service);
    let res;
    let message;

    if (service.id) {
      // ✅ Send full service object (now includes serviceName)
      console.log("📡 Sending update payload:", service);
      res = await handleServiceUpdate(service);
      message = "Updated";
    } else {
      // ✅ Pass serviceName as first argument
      console.log("📡 Sending create with name:", service.serviceName);
      res = await handleServiceCreate(
        service.serviceName, // ✅
        service.imageUrl,
        service.isGeneric
      );
      message = "Created";
    }

    if (res?.metadata?.status === apiRequestStatuses.SUCCESS) {
      fetchServices(searchTerm, pagination.currentPage);
      dispatch(
        notification({
          open: true,
          type: "success",
          message: `Service ${message} successfully!`,
        })
      );
      setOpen(false);
    } else {
      console.error("❌ API response error:", res);
      dispatch(
        notification({
          open: true,
          type: "error",
          message: "Operation failed. Check console.",
        })
      );
    }
  };

  const handlePageChange = (event, newPage) => {
    setPagination((p) => ({ ...p, currentPage: newPage }));
    fetchServices(searchTerm, newPage);
  };

  const handleSearchChange = (event) => {
    const name = event.target.value;
    setSearchTerm(name);
    fetchServices(name, 1);
  };

  const handleStatusUpdate = async (svc) => {
    const updatedService = {
      id: svc._id,
      serviceName: svc.serviceName, // ✅
      isActive: !svc.isActive,
    };

    const res = await handleServiceUpdate(updatedService);
    if (res?.metadata?.status === apiRequestStatuses.SUCCESS) {
      fetchServices(searchTerm, pagination.currentPage);
      dispatch(
        notification({
          open: true,
          type: "success",
          message: "Service status updated!",
        })
      );
    } else {
      console.error("❌ Status update failed:", res);
    }
  };

  const handleDelete = async (id) => {
    const res = await handleServiceDelete(id);
    if (res?.metadata?.status === apiRequestStatuses.SUCCESS) {
      fetchServices(searchTerm, pagination.currentPage);
      dispatch(
        notification({
          open: true,
          type: "success",
          message: "Service deleted successfully!",
        })
      );
    } else {
      console.error("❌ Delete failed:", res);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  if (loading) return <Loading />;
  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
        <Button
          onClick={() => {
            setLoading(true);
            setError(null);
            fetchServices();
          }}
        >
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{ flexGrow: 1, bgcolor: "background.default", p: 3 }}
    >
      <Toolbar />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Typography variant="h4">Discounted Service List</Typography>

        <TextField
          variant="outlined"
          placeholder="Search by service name"
          value={searchTerm}
          onChange={handleSearchChange}
          sx={{
            width: "40%",
            "& .MuiOutlinedInput-root": {
              borderRadius: "20px",
            },
          }}
        />

        <AddIcon
          sx={{
            color: "aliceblue",
            cursor: "pointer",
            background: "linear-gradient(90deg, #001F54, #9B2ECA)",
            p: 1,
            borderRadius: "50%",
          }}
          onClick={() => handleClickOpen()} // ✅ no args = create new
        />
      </Box>

      {services.length > 0 ? (
        <>
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table sx={{ minWidth: 650 }} aria-label="services table">
              <TableHead>
                <TableRow>
                  <TableCell></TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>
                    Service Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Updated On</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {services.map((svc) => (
                  <TableRow key={svc?._id} hover>
                    <TableCell>
                      {svc?.imageUrl ? (
                        <Avatar alt={svc?.serviceName} src={svc?.imageUrl} />
                      ) : (
                        <Avatar>{svc?.serviceName?.charAt(0)}</Avatar>
                      )}
                    </TableCell>
                    <TableCell>{svc?.serviceName || "-"}</TableCell>
                    <TableCell>{formatDateTime(svc?.updatedOn)}</TableCell>
                    <TableCell>
                      <CircleIcon
                        sx={{
                          color: svc?.isActive ? "#9B2ECA" : "darkgray",
                          cursor: "pointer",
                        }}
                        onClick={() => handleStatusUpdate(svc)}
                      />
                    </TableCell>
                    <TableCell>
                      <EditIcon
                        onClick={() => handleClickOpen(svc)} // ✅ pass full svc object
                        sx={{ mr: 1, cursor: "pointer" }}
                      />
                      <DeleteIcon
                        onClick={() => handleDelete(svc?._id)}
                        sx={{ cursor: "pointer" }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Pagination
            count={pagination.totalPages}
            page={pagination.currentPage}
            onChange={handlePageChange}
            sx={{ display: "flex", justifyContent: "center" }}
          />
        </>
      ) : (
        <Typography variant="body1" sx={{ textAlign: "center", py: 5 }}>
          No Services found. Create your first service!
        </Typography>
      )}

      <Dialog
        open={open}
        onClose={handleClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit, // ✅ use onSubmit for better UX
          sx: { width: "600px" },
        }}
      >
        <DialogTitle>{service.id ? "Edit Service" : "Add Service"}</DialogTitle>
        <DialogContent
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <TextField
              autoFocus
              required
              margin="dense"
              id="serviceName"
              name="serviceName" // ✅
              label="Service Name"
              value={service.serviceName} // ✅
              onChange={handleChange}
              style={{ width: "48%" }}
              variant="standard"
            />

            <TextField
              margin="dense"
              id="imageUrl"
              name="imageUrl"
              label="Image Url (optional)"
              value={service.imageUrl}
              onChange={handleChange}
              style={{ width: "48%" }}
              variant="standard"
            />
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={service.isGeneric || false}
                onChange={handleChange}
                name="isGeneric"
              />
            }
            label="Is Generic"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            {service.id ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Services;
